import { Button } from "@groot/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@groot/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@groot/ui/dialog";
import { toast } from "@/lib/toast";
import { apiClient } from "@/services/api";
import { json } from "@codemirror/lang-json";
import CodeMirror from "@uiw/react-codemirror";
import { Download, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";

interface FileViewerProps {
  filePath: string;
  fileName: string;
  onClose: () => void;
}

export function FileViewer({ filePath, fileName, onClose }: FileViewerProps) {
  const [fileUrl, setFileUrl] = useState<string>("");
  const [fileContent, setFileContent] = useState<string>("");
  const [isLoading, setIsLoading] = useState(true);

  const fileExtension = fileName.split(".").pop()?.toLowerCase() || "";

  useEffect(() => {
    const loadFile = async () => {
      setIsLoading(true);
      try {
        const blob = await apiClient.downloadFile(filePath);
        const url = window.URL.createObjectURL(blob);
        setFileUrl(url);

        // For text-based files, read content
        if (["txt", "json", "csv", "md", "html", "xml", "log"].includes(fileExtension)) {
          const text = await blob.text();
          setFileContent(text);
        }
      } catch {
        toast({
          title: "Error",
          description: "Failed to load file",
          variant: "destructive",
        });
        onClose();
      } finally {
        setIsLoading(false);
      }
    };

    loadFile();

    // Cleanup
    return () => {
      if (fileUrl) {
        window.URL.revokeObjectURL(fileUrl);
      }
    };
  }, [fileUrl, fileExtension, onClose, toast, filePath]);

  const handleDownload = () => {
    const a = document.createElement("a");
    a.href = fileUrl;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center h-[600px]">
          <Loader2 className="size-8 animate-spin" />
        </div>
      );
    }

    // PDF
    if (fileExtension === "pdf") {
      return <iframe src={fileUrl} className="w-full h-[600px] border-0" title={fileName} />;
    }

    // Images
    if (["jpg", "jpeg", "png", "gif", "svg", "webp"].includes(fileExtension)) {
      return (
        <div className="flex items-center justify-center p-4">
          <img src={fileUrl} alt={fileName} className="max-w-full max-h-[600px] object-contain" />
        </div>
      );
    }

    // JSON
    if (fileExtension === "json") {
      let formatted = fileContent;
      try {
        formatted = JSON.stringify(JSON.parse(fileContent), null, 2);
      } catch {
        // Use original content if parsing fails
      }
      return (
        <div className="overflow-auto max-h-[600px]">
          <CodeMirror
            value={formatted}
            theme="light"
            extensions={[json()]}
            editable={false}
            basicSetup={{
              lineNumbers: true,
              highlightActiveLineGutter: false,
              highlightActiveLine: false,
              foldGutter: true,
            }}
          />
        </div>
      );
    }

    // CSV
    if (fileExtension === "csv") {
      const lines = fileContent.split("\n");
      const headers = lines[0]?.split(",") || [];
      const rows = lines.slice(1).filter((line) => line.trim());

      return (
        <div className="overflow-auto max-h-[600px]">
          <Table>
            <TableHeader>
              {headers.map((header, i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: csv headers are static
                <TableHead key={i}>{header.trim()}</TableHead>
              ))}
            </TableHeader>
            <TableBody>
              {rows.map((row, i) => {
                const cells = row.split(",");
                return (
                  // biome-ignore lint/suspicious/noArrayIndexKey: csv rows don't have unique ids
                  <TableRow key={i}>
                    {cells.map((cell, j) => (
                      // biome-ignore lint/suspicious/noArrayIndexKey: csv cells are static
                      <TableCell key={j}>{cell.trim()}</TableCell>
                    ))}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      );
    }

    // Text files
    if (["txt", "md", "log", "html", "xml"].includes(fileExtension)) {
      return (
        <pre className="p-4 bg-muted rounded-lg overflow-auto max-h-[600px] text-sm whitespace-pre-wrap">
          <code>{fileContent}</code>
        </pre>
      );
    }

    // Unsupported file type
    return (
      <div className="flex flex-col items-center justify-center h-[400px] text-muted-foreground">
        <p className="mb-4">Preview not available for this file type</p>
        <Button onClick={handleDownload}>
          <Download className="size-4" />
          Download to view
        </Button>
      </div>
    );
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle>{fileName}</DialogTitle>
            <Button variant="outline" size="sm" onClick={handleDownload}>
              <Download className="size-4 mr-2" />
              Download
            </Button>
          </div>
        </DialogHeader>
        <div className="flex-1 overflow-auto">{renderContent()}</div>
      </DialogContent>
    </Dialog>
  );
}
